import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { postRequest } from 'apis/ApiInstence';
import { toast } from 'react-toastify';
import { FaPlus, FaTrash } from 'react-icons/fa';

const AddPackage = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [formData, setFormData] = useState({
    name: '',
    price_sqft: '',
    property_type: 'Residential',
    categories: [
      { name: '', features: [''] }
    ],
  });

  const handlePackageChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const addCategory = () => {
    setFormData(prev => ({
      ...prev,
      categories: [...prev.categories, { name: '', features: [''] }]
    }));
  };

  const removeCategory = (index) => {
    setFormData(prev => ({
      ...prev,
      categories: prev.categories.filter((_, i) => i !== index)
    }));
  };

  const updateCategoryName = (catIndex, value) => {
    const updated = [...formData.categories];
    updated[catIndex].name = value;
    setFormData(prev => ({ ...prev, categories: updated }));
  };

  const addFeature = (catIndex) => {
    const updated = [...formData.categories];
    updated[catIndex].features.push('');
    setFormData(prev => ({ ...prev, categories: updated }));
  };

  const removeFeature = (catIndex, featIndex) => {
    const updated = [...formData.categories];
    updated[catIndex].features.splice(featIndex, 1);
    setFormData(prev => ({ ...prev, categories: updated }));
  };

  const updateFeature = (catIndex, featIndex, value) => {
    const updated = [...formData.categories];
    updated[catIndex].features[featIndex] = value;
    setFormData(prev => ({ ...prev, categories: updated }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    if (!formData.name || !formData.price_sqft) {
      toast.error('Please fill package name and price');
      setLoading(false);
      return;
    }
    if (parseFloat(formData.price_sqft) <= 0) {
      toast.error('Price per sq.ft must be greater than 0');
      setLoading(false);
      return;
    }

    const validCategories = formData.categories.every(cat =>
      cat.name.trim() !== '' && cat.features.some(f => f.trim() !== '')
    );
    if (!validCategories) {
      toast.error('Each category must have a name and at least one feature');
      setLoading(false);
      return;
    }

    const payload = {
      name: formData.name,
      price_sqft: formData.price_sqft,
      property_type: formData.property_type,
      categories: formData.categories.map(cat => ({
        name: cat.name.trim(),
        features: cat.features.filter(f => f.trim() !== '')
      }))
    };

    try {
      const token = localStorage.getItem('userToken') || sessionStorage.getItem('userToken');
      if (!token) {
        toast.error('Authentication token missing. Please login again.');
        navigate('/signin');
        return;
      }

      await postRequest('package/full', payload, {
        Authorization: `Bearer ${token}`,
        'Content-Type': 'application/json',
      });

      toast.success('Package created successfully!');
      navigate('/packages');
    } catch (error) {
      console.error('Error creating package:', error);
      toast.error(error.response?.data?.message || 'Failed to create package');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="container-fluid">
      <div className="mainheadig mx-4">
        <h4 className="font-weight-bold">Configure New Package</h4>
        <p className="text-muted" style={{marginTop: '-1.5rem', marginBottom: '2rem'}}>Define service packages and their inclusive features for client estimates.</p>
      </div>

      <form onSubmit={handleSubmit} className="form-style mx-4">
        <div className="row">
          <div className="col-12 mb-4">
            <h5 className="font-weight-bold text-primary" style={{fontSize: '1.1rem', borderLeft: '4px solid var(--primary)', paddingLeft: '1rem'}}>Basic Configuration</h5>
          </div>

          <div className="col-md-4 mb-4">
            <label htmlFor="packageName" className="lables">Package Name *</label>
            <input
              type="text"
              id="packageName"
              name="name"
              className="form-control"
              value={formData.name}
              onChange={handlePackageChange}
              required
              placeholder="e.g., Basic, Classic, Premium"
            />
          </div>

          <div className="col-md-4 mb-4">
            <label htmlFor="packagePrice" className="lables">Price (₹/sq.ft) *</label>
            <input
              type="number"
              id="packagePrice"
              name="price_sqft"
              className="form-control"
              value={formData.price_sqft}
              onChange={handlePackageChange}
              required
              step="0.01"
              min="0"
              placeholder="e.g., 1800"
            />
          </div>

          <div className="col-md-4 mb-4">
            <label htmlFor="propertyType" className="lables">Property Type *</label>
            <select
              id="propertyType"
              name="property_type"
              className="form-control"
              value={formData.property_type}
              onChange={handlePackageChange}
              required
            >
              <option value="Residential">Residential</option>
              <option value="Commercial">Commercial</option>
              <option value="Industrial">Industrial</option>
              <option value="Townships">Townships</option>
              <option value="Farmhouses">Farmhouses</option>
            </select>
          </div>
        </div>

        <div className="col-12 mt-4 mb-4 px-0">
          <h5 className="font-weight-bold text-primary" style={{fontSize: '1.1rem', borderLeft: '4px solid var(--primary)', paddingLeft: '1rem'}}>Categories & Detailed Features</h5>
        </div>

        {formData.categories.map((category, catIndex) => (
          <div key={catIndex} className="card p-4 mb-4 border-0 shadow-sm" style={{background: '#f8f9fa', borderRadius: '12px'}}>
            <div className="row align-items-end">
              <div className="col-md-8">
                <label className="lables" htmlFor={`catName_${catIndex}`}>Category Name</label>
                <input
                  type="text"
                  id={`catName_${catIndex}`}
                  className="form-control"
                  value={category.name}
                  onChange={(e) => updateCategoryName(catIndex, e.target.value)}
                  placeholder="e.g., Structure, Kitchen, Bathroom"
                  required
                />
              </div>
              <div className="col-md-4 text-right">
                <button type="button" className="btn btn-outline-danger" onClick={() => removeCategory(catIndex)}>
                  <FaTrash className="mr-2" /> Remove Category
                </button>
              </div>
            </div>

            <div className="mt-4">
              <div className="lables">Features List</div>
              <div className="row">
                {category.features.map((feature, featIndex) => (
                  <div key={featIndex} className="col-md-6 mb-3">
                    <div className="input-group shadow-sm" style={{borderRadius: '10px', overflow: 'hidden'}}>
                      <input
                        type="text"
                        className="form-control border-0"
                        value={feature}
                        onChange={(e) => updateFeature(catIndex, featIndex, e.target.value)}
                        placeholder="Enter feature description"
                        style={{background: '#fff'}}
                      />
                      <div className="input-group-append">
                        <button
                          type="button"
                          className="btn btn-white text-danger border-0"
                          onClick={() => removeFeature(catIndex, featIndex)}
                          disabled={category.features.length === 1}
                        >
                          <FaTrash size={14} />
                        </button>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
              <button type="button" className="btn btn-sm btn-link text-primary font-weight-bold p-0" onClick={() => addFeature(catIndex)}>
                <FaPlus size={12} className="mr-1" /> Add Another Feature
              </button>
            </div>
          </div>
        ))}

        <div className="mb-5">
          <button type="button" className="btn btn-outline-primary" onClick={addCategory}>
            <FaPlus className="mr-2" /> Add New Category
          </button>
        </div>

        <hr className="my-4" />

        <div className="d-flex justify-content-end">
          <button type="button" className="btn btn-light mr-3" onClick={() => navigate('/packages')}>Cancel</button>
          <button type="submit" className="btn addbtn" disabled={loading} style={{minWidth: '200px'}}>
            {loading ? (
              <>
                <span className="spinner-border spinner-border-sm mr-2" role="status" aria-hidden="true"></span>
                Saving...
              </>
            ) : (
              'Create Package'
            )}
          </button>
        </div>
      </form>
    </div>
  );
};

export default AddPackage;